/**
 * Painter's-order decal layering for baked Red Baron meshes.
 *
 * The original engine drew polygons back-to-front in file order with no
 * depth buffer, so insignia (crosses, roundels) are *coplanar* polygons
 * listed after the surface they decorate. Under a modern depth buffer those
 * faces z-fight. This module reconstructs the intent from the data alone:
 * a face that is coplanar with AND overlaps an earlier face is a decal
 * layer, and gets nudged along the shared normal by a few millimetres per
 * layer — invisible at any viewing distance, decisive for the depth test.
 *
 * Faces that merely share a plane without overlapping (adjacent panels of
 * one wing skin) stay at layer 0, so panel seams don't crack open.
 */

/**
 * Coplanarity tolerances: normals within ~2.5°, plane offsets within 15 mm.
 *
 * Exported because the *region-wide* pass (`three/plate-paint-order.ts`) asks
 * the same question one model up — "are these two faces the same surface?" —
 * and two answers to that question that could drift apart is the bug this
 * codebase keeps re-learning. One definition, two callers.
 */
export const NORMAL_DOT_MIN = 0.999;
export const PLANE_DISTANCE_TOLERANCE_M = 0.015;
/** Two triangles must interpenetrate by more than this (in 2D) to count as overlapping — shared edges don't. */
const OVERLAP_MARGIN_M = 0.002;
/** Nudge per decal layer. 4 mm: below visual notice, far above depth-buffer resolution at gameplay ranges. */
export const DECAL_LAYER_OFFSET_M = 0.004;

type Vec3 = readonly [number, number, number];
type Vec2 = readonly [number, number];
/** Exported alone of the three: `plate-paint-order.ts` holds these between its own two passes, and `Vec2`/`Vec3` are names other packages already use for their own shapes. */
export type Triangle2 = readonly [Vec2, Vec2, Vec2];

interface PlaneGroup {
  readonly normal: Vec3;
  readonly distance: number;
  /** 2D projections (in the plane's dominant axes) of member triangles, with their assigned layer. */
  readonly members: { triangle: Triangle2; layer: number }[];
}

/** Projects a 3D point onto the two axes least aligned with the plane normal. */
export function project(point: Vec3, normal: Vec3): Vec2 {
  const ax = Math.abs(normal[0]);
  const ay = Math.abs(normal[1]);
  const az = Math.abs(normal[2]);
  if (az >= ax && az >= ay) return [point[0], point[1]];
  if (ay >= ax) return [point[0], point[2]];
  return [point[1], point[2]];
}

/** Smallest signed interval gap between the two triangles' projections onto `axis`; positive = separated. */
function axisGap(a: Triangle2, b: Triangle2, axis: Vec2): number {
  let minA = Infinity;
  let maxA = -Infinity;
  let minB = Infinity;
  let maxB = -Infinity;
  for (const p of a) {
    const d = p[0] * axis[0] + p[1] * axis[1];
    minA = Math.min(minA, d);
    maxA = Math.max(maxA, d);
  }
  for (const p of b) {
    const d = p[0] * axis[0] + p[1] * axis[1];
    minB = Math.min(minB, d);
    maxB = Math.max(maxB, d);
  }
  return Math.max(minB - maxA, minA - maxB);
}

/** Separating-axis test in 2D; overlap only counts past `OVERLAP_MARGIN_M` so edge-adjacent faces stay separate. */
export function trianglesOverlap(a: Triangle2, b: Triangle2): boolean {
  for (const tri of [a, b]) {
    for (let i = 0; i < 3; i++) {
      const p = tri[i]!;
      const q = tri[(i + 1) % 3]!;
      const edge: Vec2 = [q[0] - p[0], q[1] - p[1]];
      const length = Math.hypot(edge[0], edge[1]);
      if (length === 0) continue;
      const axis: Vec2 = [-edge[1] / length, edge[0] / length];
      if (axisGap(a, b, axis) > -OVERLAP_MARGIN_M) return false;
    }
  }
  return true;
}

/** Flips `n` (and the plane distance with it) into a canonical hemisphere so anti-parallel normals share one plane group. */
function canonicalPlane(n: Vec3, distance: number): { normal: Vec3; distance: number } {
  const flip = n[0] < -1e-9 || (Math.abs(n[0]) <= 1e-9 && (n[1] < -1e-9 || (Math.abs(n[1]) <= 1e-9 && n[2] < 0)));
  return flip ? { normal: [-n[0], -n[1], -n[2]], distance: -distance } : { normal: n, distance };
}

/**
 * Assigns a decal layer to every triangle: 0 for base surfaces, 1+ for each
 * successive coplanar overlap in draw order. Grouping is by UNSIGNED plane,
 * so two-sided skins — coplanar face pairs with opposite normals, which the
 * original painter's-order renderer drew without conflict but a depth buffer
 * fights over (user report: rudder/wing z-fighting) — also layer apart. The
 * displacement is applied along each face's OWN normal downstream, so an
 * opposite-facing pair separates in opposite directions, each visible from
 * its side. `corners` and `normals` are one entry per triangle.
 */
export function decalLayers(corners: readonly (readonly [Vec3, Vec3, Vec3])[], normals: readonly Vec3[]): Uint16Array {
  const layers = new Uint16Array(corners.length);
  const groups: PlaneGroup[] = [];

  for (let t = 0; t < corners.length; t++) {
    const n = normals[t]!;
    const tri = corners[t]!;
    const rawDistance = tri[0][0] * n[0] + tri[0][1] * n[1] + tri[0][2] * n[2];
    const plane = canonicalPlane(n, rawDistance);

    let group: PlaneGroup | undefined;
    for (const candidate of groups) {
      const dot = candidate.normal[0] * plane.normal[0] + candidate.normal[1] * plane.normal[1] + candidate.normal[2] * plane.normal[2];
      if (dot >= NORMAL_DOT_MIN && Math.abs(candidate.distance - plane.distance) <= PLANE_DISTANCE_TOLERANCE_M) {
        group = candidate;
        break;
      }
    }
    if (!group) {
      group = { normal: plane.normal, distance: plane.distance, members: [] };
      groups.push(group);
    }

    const projected: Triangle2 = [project(tri[0], group.normal), project(tri[1], group.normal), project(tri[2], group.normal)];
    let layer = 0;
    for (const member of group.members) {
      if (member.layer >= layer && trianglesOverlap(projected, member.triangle)) {
        layer = member.layer + 1;
      }
    }
    group.members.push({ triangle: projected, layer });
    layers[t] = layer;
  }

  return layers;
}
